import { useEffect, useRef, useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Home,
  Newspaper,
  Shield,
  ShoppingBag,
  Menu,
  BarChart3,
  LogIn,
  LogOut,
  Trophy,
  ChevronRight,
  X,
  CalendarDays,
  Users,
  MapPin,
  Flag,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { tap } from "@/lib/haptics";
import { supabase } from "@/integrations/supabase/client";
import { LogoMark } from "@/components/site/logo";
import { fixturesQuery, newsByCategoryQuery } from "@/lib/site-queries";

type Tab = {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  match: (p: string) => boolean;
};

const TABS: Tab[] = [
  { to: "/", label: "Home", icon: Home, match: (p) => p === "/" },
  { to: "/news", label: "News", icon: Newspaper, match: (p) => p.startsWith("/news") },
  { to: "/national-team", label: "NT", icon: Shield, match: (p) => p.startsWith("/national-team") },
  { to: "/shop", label: "Shop", icon: ShoppingBag, match: (p) => p.startsWith("/shop") },
];

// Quick-links inside the fold-out: a 2026 World Cup hub instead of duplicating the dock.
const WC_LINKS: { to: string; label: string; desc: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { to: "/national-team/world-cup", label: "WC Hub", desc: "Tournament guide", icon: Trophy },
  { to: "/national-team/fixtures", label: "Fixtures", desc: "Knockout bracket", icon: CalendarDays },
  { to: "/national-team/statistics", label: "Stats", desc: "Form & scorers", icon: BarChart3 },
  { to: "/national-team/squad", label: "Squad", desc: "Scaloni's 26", icon: Users },
];

// Public, well-known 2026 World Cup facts. Static editorial scaffold so the
// menu has rich content even before WC fixtures land in the database.
const WC_FACTS = [
  { icon: Flag, label: "Teams", value: "48" },
  { icon: Trophy, label: "Groups", value: "12" },
  { icon: CalendarDays, label: "Matches", value: "104" },
  { icon: MapPin, label: "Final", value: "NY/NJ" },
];

function fmtKickoff(iso: string) {
  try {
    const d = new Date(iso);
    return (
      new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "UTC",
      }).format(d) + " UTC"
    );
  } catch {
    return "";
  }
}

/**
 * Unified bottom dock + fold-out menu. The menu surfaces a World Cup 2026
 * hub (next fixtures, news, stats, squad) rather than re-listing the dock's
 * own destinations — the dock already covers Home/News/NT/Shop.
 */
export function BottomNav() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [open, setOpen] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSignedIn(!!data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSignedIn(!!s));
    return () => sub.subscription.unsubscribe();
  }, []);

  // Close on route change
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Lazy-load World Cup content only when the menu is opened.
  const wcFixtures = useQuery({ ...fixturesQuery("world_cup", 6), enabled: open });
  const wcNews = useQuery({ ...newsByCategoryQuery("national_team", 3), enabled: open });

  const upcoming = (wcFixtures.data ?? []).filter(
    (f) => new Date(f.kickoff).getTime() >= Date.now() - 1000 * 60 * 60 * 2,
  );
  const fixturesToShow = (upcoming.length > 0 ? upcoming : (wcFixtures.data ?? [])).slice(0, 3);

  const closeAndTap = () => {
    tap(6);
    setOpen(false);
  };

  return (
    <>
      {/* Backdrop — clicking it closes the panel. Lighter than a modal scrim. */}
      <button
        type="button"
        aria-hidden={!open}
        tabIndex={-1}
        onClick={() => setOpen(false)}
        className={cn(
          "fixed inset-0 z-30 bg-navy/40 backdrop-blur-[2px] transition-opacity duration-150",
          open ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      />

      {/* Floating dock + anchored menu panel */}
      <div
        className="fixed inset-x-0 bottom-0 z-40 flex flex-col items-center px-2 sm:px-3 pointer-events-none"
        style={{ paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))" }}
      >
        {/* MENU PANEL — anchored above the dock, fast slide+fade */}
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-label="World Cup 2026 quick hub"
          aria-hidden={!open}
          className={cn(
            "pointer-events-auto mb-2 w-full max-w-md origin-bottom overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-navy via-navy to-[#06122c] text-primary-foreground shadow-stadium backdrop-blur-xl sm:max-w-lg",
            "transition-[opacity,transform] duration-150 ease-out will-change-transform",
            open
              ? "translate-y-0 scale-100 opacity-100"
              : "pointer-events-none translate-y-3 scale-[0.98] opacity-0",
          )}
        >
          {/* Top brand strip */}
          <div className="relative overflow-hidden border-b border-white/10">
            <div className="absolute inset-x-0 top-0 h-1 sky-bar" />
            <div className="flex items-center gap-2.5 px-4 py-3">
              <LogoMark className="h-7 w-7 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="font-condensed text-[0.6rem] font-bold uppercase tracking-[0.3em] text-sky">
                  World Cup 2026
                </p>
                <p className="truncate font-display text-sm text-primary-foreground/85">
                  Canada · Mexico · USA · 48 teams
                </p>
              </div>
              <button
                type="button"
                onClick={closeAndTap}
                aria-label="Close menu"
                className="grid h-9 w-9 place-items-center rounded-lg text-primary-foreground/80 transition-colors hover:bg-white/10 hover:text-primary-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="max-h-[68vh] overflow-y-auto px-3 pb-3 pt-3">
            {/* Tournament facts strip */}
            <ul className="grid grid-cols-4 gap-1.5">
              {WC_FACTS.map((f) => {
                const Icon = f.icon;
                return (
                  <li
                    key={f.label}
                    className="rounded-lg border border-white/10 bg-white/5 px-2 py-2 text-center"
                  >
                    <Icon className="mx-auto h-3.5 w-3.5 text-sky" aria-hidden />
                    <p className="mt-1 font-display text-base leading-none text-primary-foreground">
                      {f.value}
                    </p>
                    <p className="mt-0.5 font-condensed text-[0.55rem] font-bold uppercase tracking-widest text-primary-foreground/55">
                      {f.label}
                    </p>
                  </li>
                );
              })}
            </ul>

            {/* Upcoming WC fixtures */}
            <p className="mt-4 px-1 pb-1.5 font-condensed text-[0.6rem] font-bold uppercase tracking-[0.3em] text-primary-foreground/45">
              {upcoming.length > 0 ? "Next at the World Cup" : "World Cup fixtures"}
            </p>
            {wcFixtures.isLoading ? (
              <ul className="flex flex-col gap-1.5" aria-hidden>
                {[0, 1, 2].map((i) => (
                  <li key={i} className="h-12 animate-pulse rounded-lg bg-white/5" />
                ))}
              </ul>
            ) : fixturesToShow.length === 0 ? (
              <p className="rounded-lg border border-dashed border-white/10 px-3 py-3 text-center font-condensed text-[0.7rem] uppercase tracking-wider text-primary-foreground/55">
                Draw lands soon — check back
              </p>
            ) : (
              <ul className="flex flex-col gap-1.5">
                {fixturesToShow.map((f) => (
                  <li key={f.id}>
                    <Link
                      to="/national-team/fixtures/$id"
                      params={{ id: f.id }}
                      onClick={closeAndTap}
                      className="group flex items-center gap-3 rounded-lg border border-white/10 bg-white/5 px-3 py-2 transition-colors hover:bg-white/10"
                    >
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/20 text-sm">
                        {f.opponent_flag ?? "🏴"}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-display text-sm text-primary-foreground">
                          ARG <span className="text-primary-foreground/50">vs</span> {f.opponent}
                        </span>
                        <span className="block truncate font-condensed text-[0.6rem] uppercase tracking-wider text-primary-foreground/55">
                          {f.competition ?? "World Cup"} · {fmtKickoff(f.kickoff)}
                        </span>
                      </span>
                      <ChevronRight className="h-3.5 w-3.5 shrink-0 text-primary-foreground/40 transition-transform group-hover:translate-x-0.5" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}

            {/* WC quick links */}
            <p className="mt-4 px-1 pb-1.5 font-condensed text-[0.6rem] font-bold uppercase tracking-[0.3em] text-primary-foreground/45">
              World Cup sections
            </p>
            <ul className="grid grid-cols-4 gap-1.5">
              {WC_LINKS.map((item) => {
                const Icon = item.icon;
                const active = pathname === item.to || pathname.startsWith(item.to + "/");
                return (
                  <li key={item.to}>
                    <Link
                      to={item.to as "/"}
                      onClick={closeAndTap}
                      className={cn(
                        "flex flex-col items-center gap-1 rounded-lg border px-1.5 py-2.5 text-center transition-colors",
                        active
                          ? "border-primary/40 bg-primary/20 text-primary-foreground"
                          : "border-white/10 bg-white/5 text-primary-foreground/85 hover:bg-white/10",
                      )}
                    >
                      <Icon className="h-4 w-4 text-sky" aria-hidden />
                      <span className="font-condensed text-[0.65rem] font-bold uppercase tracking-wider">
                        {item.label}
                      </span>
                      <span className="font-condensed text-[0.55rem] uppercase tracking-wider text-primary-foreground/50">
                        {item.desc}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>

            {/* Latest WC-flavoured news */}
            <p className="mt-4 px-1 pb-1.5 font-condensed text-[0.6rem] font-bold uppercase tracking-[0.3em] text-primary-foreground/45">
              From the desk
            </p>
            {wcNews.isLoading ? (
              <ul className="flex flex-col gap-1.5" aria-hidden>
                {[0, 1, 2].map((i) => (
                  <li key={i} className="h-10 animate-pulse rounded-lg bg-white/5" />
                ))}
              </ul>
            ) : (wcNews.data ?? []).length === 0 ? (
              <p className="rounded-lg border border-dashed border-white/10 px-3 py-3 text-center font-condensed text-[0.7rem] uppercase tracking-wider text-primary-foreground/55">
                No new dispatches
              </p>
            ) : (
              <ul className="flex flex-col gap-1.5">
                {(wcNews.data ?? []).map((a) => (
                  <li key={a.id}>
                    <Link
                      to="/news/$slug"
                      params={{ slug: a.slug ?? a.id }}
                      onClick={closeAndTap}
                      className="group flex items-start gap-2 rounded-lg px-2 py-2 transition-colors hover:bg-white/5"
                    >
                      <Newspaper className="mt-0.5 h-3.5 w-3.5 shrink-0 text-sky" aria-hidden />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-display text-sm leading-tight text-primary-foreground">
                          {a.title}
                        </span>
                        {a.author && (
                          <span className="block truncate font-condensed text-[0.6rem] uppercase tracking-wider text-primary-foreground/50">
                            {a.author}
                          </span>
                        )}
                      </span>
                      <ChevronRight className="mt-1 h-3.5 w-3.5 shrink-0 text-primary-foreground/40 transition-transform group-hover:translate-x-0.5" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}

            {/* Auth row */}
            <div className="mt-4 border-t border-white/10 px-1 pt-3">
              {signedIn ? (
                <button
                  onClick={() => {
                    tap(12);
                    supabase.auth.signOut();
                    setOpen(false);
                  }}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-white/20 bg-white/5 px-3 py-2 font-condensed text-[0.7rem] font-bold uppercase tracking-widest text-primary-foreground hover:bg-white/10 active:scale-95"
                >
                  <LogOut className="h-3.5 w-3.5" /> Sign out
                </button>
              ) : (
                <Link
                  to="/log-in"
                  onClick={closeAndTap}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-primary via-primary to-sky px-3 py-2 font-condensed text-[0.7rem] font-bold uppercase tracking-widest text-primary-foreground transition-all hover:opacity-95 active:scale-95"
                >
                  <LogIn className="h-3.5 w-3.5" /> Log in / Sign up
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* DOCK */}
        <nav
          aria-label="Primary"
          className="pointer-events-auto flex w-full max-w-md items-stretch gap-0.5 rounded-2xl border border-white/10 bg-navy/90 p-1 text-primary-foreground shadow-stadium backdrop-blur-xl sm:max-w-lg sm:gap-1 sm:p-1.5"
        >
          {TABS.map((t) => {
            const active = t.match(pathname);
            const Icon = t.icon;
            return (
              <Link
                key={t.to}
                to={t.to as "/"}
                onClick={() => tap(6)}
                aria-label={`${t.label}${active ? " (current page)" : ""}`}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "group relative flex min-h-[48px] flex-1 flex-col items-center justify-center gap-0.5 rounded-xl px-1 py-1.5 text-[0.6rem] font-condensed font-bold uppercase tracking-[0.16em] transition-all active:scale-95 sm:px-2 sm:py-2 sm:tracking-[0.18em]",
                  "outline-none focus-visible:ring-2 focus-visible:ring-sky focus-visible:ring-offset-2 focus-visible:ring-offset-navy",
                  active
                    ? "bg-gradient-to-b from-primary/30 via-primary/15 to-transparent text-primary-foreground"
                    : "text-primary-foreground/65 hover:text-primary-foreground",
                )}
              >
                {active && (
                  <span
                    aria-hidden
                    className="absolute inset-x-3 top-0 h-0.5 rounded-full bg-sky shadow-[0_0_10px_rgba(108,191,255,0.7)]"
                  />
                )}
                <Icon aria-hidden className={cn("h-5 w-5 transition-transform", active && "scale-110")} />
                <span>{t.label}</span>
              </Link>
            );
          })}

          <span aria-hidden className="my-2 w-px bg-white/10" />

          <button
            type="button"
            onClick={() => {
              tap(open ? 6 : 10);
              setOpen((v) => !v);
            }}
            aria-label={open ? "Close World Cup menu" : "Open World Cup menu"}
            aria-expanded={open}
            aria-haspopup="dialog"
            className={cn(
              "relative flex min-h-[48px] flex-1 flex-col items-center justify-center gap-0.5 rounded-xl px-1 py-1.5 text-[0.6rem] font-condensed font-bold uppercase tracking-[0.16em] transition-all active:scale-95 sm:px-2 sm:py-2 sm:tracking-[0.18em]",
              "outline-none focus-visible:ring-2 focus-visible:ring-sky focus-visible:ring-offset-2 focus-visible:ring-offset-navy",
              open
                ? "bg-gradient-to-b from-sky/30 via-sky/15 to-transparent text-primary-foreground"
                : "text-primary-foreground/65 hover:text-primary-foreground",
            )}
          >
            {open && (
              <span
                aria-hidden
                className="absolute inset-x-3 top-0 h-0.5 rounded-full bg-sky shadow-[0_0_10px_rgba(108,191,255,0.7)]"
              />
            )}
            <span className="relative grid h-5 w-5 place-items-center">
              <Menu
                aria-hidden
                className={cn(
                  "h-5 w-5 transition-all duration-150",
                  open ? "scale-50 rotate-90 opacity-0" : "scale-100 rotate-0 opacity-100",
                )}
              />
              <X
                aria-hidden
                className={cn(
                  "absolute h-5 w-5 transition-all duration-150",
                  open ? "scale-100 rotate-0 opacity-100" : "scale-50 -rotate-90 opacity-0",
                )}
              />
            </span>
            <span>{open ? "Close" : "WC 26"}</span>
          </button>
        </nav>
      </div>
    </>
  );
}
